import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isBlank, notBlank } from "../../util/ext";
import Paper from "@mui/material/Paper";
import { useConfirm } from "../../common/use-confirm";
import {
  any,
  append,
  concat,
  difference,
  map,
  prop,
  propEq,
  reject,
  sortBy,
  without,
} from "ramda";
import Grid from "@mui/material/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import DialogContentText from "@mui/material/DialogContentText";
import { useAudioTypes } from "../cspServices";
import { caseInsensitiveSort } from "../../util/sorts";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const sortAudioTypes = caseInsensitiveSort("display");

const AudioTypesDialog = ({ open, onCancel, onApply, submissionType }) => {
  const classes = useStyles();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);

  const audioTypesResponse = useAudioTypes();
  useEffect(() => {
    if (audioTypesResponse.isSuccess) {
      if (notBlank(submissionType.audioTypes)) {
        setSelected(submissionType.audioTypes);
        const unused = reject(
          role => any(propEq("name", role.name), submissionType.audioTypes),
          audioTypesResponse.data,
        );

        setAvailable(
          sortBy(prop("name"), concat(submissionType.audioTypes, unused)),
        );
      } else {
        setAvailable(audioTypesResponse.data);
      }
    }
  }, [
    audioTypesResponse.isSuccess,
    audioTypesResponse.data,
    submissionType.audioTypes,
  ]);

  const handleClose = useCallback(() => {
    const previous = notBlank(submissionType.audioTypes)
      ? submissionType.audioTypes
      : [];
    if (isBlank(difference(selected, previous))) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected audio types without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selected, submissionType.audioTypes, showConfirm, onCancel]);

  const handleApply = useCallback(() => {
    onApply(selected);
  }, [onApply, selected]);

  const handleSelected = type => event => {
    if (event.target.checked) {
      setSelected(prev => append(type, prev));
    } else {
      setSelected(prev => without([type], prev));
    }
  };

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle id="add-submission-types-dialog-title">
        Update Audio Types
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`Check the Audio Types that should allowed for ${submissionType.displayName} submissions`}
        </DialogContentText>
        <Paper>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              {map(
                type => (
                  <Grid key={type.name} item md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={any(propEq("name", type.name), selected)}
                          onChange={handleSelected(type)}
                          name={type.name}
                          color="primary"
                        />
                      }
                      label={type.display}
                    />
                  </Grid>
                ),
                sortAudioTypes(available),
              )}
            </Grid>
          </Box>
        </Paper>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudioTypesDialog;
